import { createApp } from 'vue';
import { createPinia } from 'pinia';

import * as Sentry from '@sentry/vue';

import App from './App.vue';
import router from './router/router';

import '@/assets/tailwind.css';
import '@prospective/lithium/lithium.css';
import '@/assets/lead.scss';

import VToast, { POSITION as VTPosition, TYPE as VTType } from 'vue-toastification';
import type { PluginOptions } from 'vue-toastification';

import vSelect from 'vue-select';
import 'vue-select/dist/vue-select.css';

import { Dropdown, vTooltip } from 'floating-vue';
import 'floating-vue/dist/style.css';
import FloatingVue from 'floating-vue';

import { authorizeClient } from './api/client';
import { brand } from '@/config/brands';
import { applyBrandTheme } from '@/config/brands/applyBrandTheme';

import translator from './locale/translator.js';
import { TranslatorPlugin } from '@prospective/lithium';

import { Dispatcher } from '@/events/dispatcher';

import PreviewModalPlugin from '@/plugins/previewModal';
import AutopilotRouterPlugin from '@/autopilot/routerPlugin';

import 'apexcharts';
import { APEX_LOCALES, getApexLocaleName } from '@/locale/apexchartsLocale';

applyBrandTheme(brand);

// Register ApexCharts locales + pick the active one from the UI language so every chart's tooltip
// format follows it without per-chart config. Note: Apex reads these from `Apex.chart.locales` /
// `Apex.chart.defaultLocale` (merged into each chart's config), NOT from top-level `Apex.locales`.
if (typeof window !== 'undefined' && window.Apex) {
  window.Apex.chart = {
    ...(window.Apex.chart as object | undefined),
    locales: APEX_LOCALES,
    defaultLocale: getApexLocaleName(),
  };
}

const app = createApp(App);
const pinia = createPinia();
const dispatcher = new Dispatcher();
const hostname = import.meta.env.VITE_PJAS_HOSTNAME ?? `${window.location.protocol}//${window.location.hostname}`;

const toastOptions: PluginOptions = {
  timeout: 7000,
  transition: 'Vue-Toastification__bounce',
  position: VTPosition.TOP_CENTER,
  toastDefaults: {
    [VTType.ERROR]: {
      icon: 'ti ti-alert-octagon',
    },
    [VTType.WARNING]: {
      icon: 'ti ti-alert-triangle',
    },
    [VTType.SUCCESS]: {
      icon: 'ti ti-check',
    },
  },
};

// Sentry runs on deployed stage + production only — PROD is true for any `vite build`
// output and false under `vite dev`, so local dev stays off Sentry entirely.
// tracePropagationTargets is scoped to `${hostname}/v1` so Sentry only traces our API
// calls, not static assets or third-party requests.
if (import.meta.env.PROD) {
  Sentry.init({
    app,
    dsn: 'https://822f2198c2eb508c77428d915f8b58d2@o927288.ingest.sentry.io/4506592492781568',
    integrations: [
      Sentry.browserTracingIntegration({
        router,
      }),
      Sentry.replayIntegration({
        maskAllText: false,
        blockAllMedia: false,
      }),
    ],
    tracePropagationTargets: [`${hostname}/v1`],
    tracesSampleRate: 1.0,
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
  });
}

authorizeClient().then(async () => {
  app.use(VToast, toastOptions);
  app.component('VSelect', vSelect);

  app.component('VDropdown', Dropdown);
  app.directive('tooltip', vTooltip);
  FloatingVue.options.themes.tooltip.popperHideTriggers = ['hover'];

  app.provide('dispatcher', dispatcher);
  app.use(PreviewModalPlugin, dispatcher);

  app.use(pinia);
  app.use(router);
  app.use(AutopilotRouterPlugin, { router });
  app.use(TranslatorPlugin, { translator });

  app.mount('#app');
});
